/**
 * Usage statistics on Mojo Up AI Cloud. Discovery names a Google Analytics measurement id only on
 * Cloud, and nothing loads until the person agrees: the consent strip asks once, and Settings →
 * Privacy changes the answer. The answer is kept in this browser.
 *
 * What is sent: page views by the page's shape (`/projects/:id`, never an id, a name or a query
 * string) and nothing else. No advertising signals, no Google signals.
 */

import { routes } from './api/client.ts';

export type UsageConsent = 'granted' | 'denied' | undefined;

const KEY = 'aioe.usageStatistics';
const GTAG = 'https://www.googletagmanager.com/gtag/js';

let measurementId: string | undefined;
let loaded = false;
const listeners = new Set<() => void>();

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function read(): UsageConsent {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'granted' || v === 'denied' ? v : undefined;
  } catch {
    return undefined;
  }
}

/** Learns from discovery whether this deployment counts usage; loads the tag when the person already agreed. */
export async function startAnalytics(): Promise<void> {
  // Discovery names an id only on Cloud: Enterprise never loads anything.
  try {
    measurementId = (await routes.discovery()).analyticsId;
  } catch {
    return;
  }
  if (measurementId && read() === 'granted') load();
  for (const l of listeners) l();
}

/** Whether this deployment counts usage at all: Cloud with a measurement id. */
export const analyticsOffered = (): boolean => measurementId !== undefined;
export const usageConsent = (): UsageConsent => read();
export function subscribeAnalytics(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function setUsageConsent(next: 'granted' | 'denied'): void {
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // Storage refused (a private window): the answer lasts for this page only.
  }
  if (next === 'granted') load();
  else if (measurementId) {
    disable(true);
    window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
    // The cookies the tag set go with it.
    for (const c of document.cookie.split(';')) {
      const name = c.split('=')[0]!.trim();
      if (name === '_ga' || name.startsWith('_ga_')) document.cookie = `${name}=; Max-Age=0; path=/; domain=.${location.hostname.split('.').slice(-2).join('.')}`;
    }
  }
  for (const l of listeners) l();
}

/** Google's own off switch for a measurement id, read by the tag before it sends anything. */
const disable = (off: boolean) => ((window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = off);

function load(): void {
  if (!measurementId || loaded) return;
  loaded = true;
  disable(false);
  window.dataLayer = window.dataLayer ?? [];
  // gtag reads `arguments`, not an array: the standard shim.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false });
  const s = document.createElement('script');
  s.src = `${GTAG}?id=${encodeURIComponent(measurementId)}`;
  s.async = true;
  document.head.appendChild(s);
  pageView(location.pathname);
}

/** Path segments kept as they are: the console's own words. Anything else is an id or a name, sent as `:id`. */
const WORDS = new Set([
  'workflows', 'workflow-runs', 'teamwork', 'members',
  // Tabs (settings, foundry, compute) are the console's words too.
  'overview', 'account', 'accounts', 'notifications', 'approvals', 'away', 'date-time', 'appearance', 'privacy', 'organisation', 'roles', 'subscriptions', 'data', 'audit', 'integrations', 'readiness', 'platform', 'acknowledgements',
]);

/** The page's shape: the first segment, then the console's own words, and `:id` for everything else. */
export function pageShape(pathname: string): string {
  const [first, ...rest] = pathname.split('/').filter(Boolean);
  if (!first) return '/';
  return `/${[first, ...rest.map((s) => (WORDS.has(s) ? s : ':id'))].join('/')}`;
}

/** A page view, when the person agreed. */
export function pageView(pathname: string): void {
  if (!loaded || !measurementId || read() !== 'granted') return;
  const path = pageShape(pathname);
  window.gtag?.('event', 'page_view', { page_location: `${location.origin}${path}`, page_path: path, page_title: document.title });
}
